import { Geometry, Graphics, Mesh, Shader } from 'pixi.js';
import { SVG, SVGConfig } from './svg-element';
import { GeneralShape } from '../general/general-shape';

const MAX_STOPS = 8;

export enum GradientDirection {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
  Diagonal = 'diagonal',
}

function hexToRgb(hex) {
  // Remove the hash (#) if present
  hex = hex.replace(/^#/, '');

  // Parse the r, g, b values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // -- // -- //
  return [r, g, b];
}

export type GradientStop = { color: string; stop?: number };

function hexToRgba01(hex: string, alpha = 1): [number, number, number, number] {
  hex = hex.replace(/^#/, '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  return [r, g, b, alpha];
}

function normalizeStops(stops: GradientStop[]): Required<GradientStop>[] {
  const n = Math.max(1, stops.length);
  const hasAnyStop = stops.some(s => typeof s.stop === 'number');

  // If none provided -> evenly distribute
  if (!hasAnyStop) {
    return stops.map((s, i) => ({
      color: s.color,
      stop: n === 1 ? 0 : i / (n - 1),
    }));
  }

  // If some provided, fill missing ones evenly between nearest neighbors
  const out = stops.map(s => ({
    color: s.color,
    stop: s.stop as number | undefined,
  }));

  // Ensure first/last exist
  if (out[0].stop == null) out[0].stop = 0;
  if (out[n - 1].stop == null) out[n - 1].stop = 1;

  // Fill gaps
  let i = 0;
  while (i < n) {
    if (out[i].stop != null) {
      i++;
      continue;
    }
    const start = i - 1;
    let end = i;
    while (end < n && out[end].stop == null) end++;
    const a = out[start].stop!;
    const b = out[end].stop!;
    const gap = end - start;
    for (let k = 1; k < gap; k++) {
      out[start + k].stop = a + (b - a) * (k / gap);
    }
    i = end + 1;
  }

  // Clamp + sort by stop (important!)
  return out
    .map(s => ({ color: s.color, stop: Math.min(1, Math.max(0, s.stop!)) }))
    .sort((a, b) => a.stop - b.stop);
}

export class RectangleElement extends SVG<{
  width: number;
  height: number;
  r?: number;
}> {
  get vertexShader() {
    return `
      attribute vec2 aVertexPosition;
      attribute vec2 aTextureCoord;
      varying vec2 vTextureCoord;
      
      uniform mat3 translationMatrix;
      uniform mat3 projectionMatrix;
      
      void main() {
        // Apply translation and projection matrices
        gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
        vTextureCoord = aTextureCoord;
      }
    `;
  }

  getFragmentShader(direction = GradientDirection.Horizontal) {
    let tExpr: string;

    switch (direction) {
      case GradientDirection.Diagonal:
        tExpr = '(vTextureCoord.x + vTextureCoord.y) * 0.5';
        break;
      case GradientDirection.Vertical:
        tExpr = 'vTextureCoord.y';
        break;
      case GradientDirection.Horizontal:
      default:
        tExpr = 'vTextureCoord.x';
        break;
    }

    return `
    precision mediump float;

    varying vec2 vTextureCoord;

    uniform int uStopCount;
    uniform vec4 uColors[${MAX_STOPS}];
    uniform float uStops[${MAX_STOPS}];
    uniform float uOpacity;

    float saturate(float x) { return clamp(x, 0.0, 1.0); }

    void main() {
      float t = saturate(${tExpr});

      // Default: first color
      vec4 color = uColors[0];

      // Find the segment that contains t and mix
      for (int i = 0; i < ${MAX_STOPS - 1}; i++) {
        if (i >= uStopCount - 1) break;

        float a = uStops[i];
        float b = uStops[i + 1];

        // If t is inside this segment
        if (t >= a && t <= b) {
          float localT = (b == a) ? 0.0 : (t - a) / (b - a);
          color = mix(uColors[i], uColors[i + 1], saturate(localT));
        }
      }

      gl_FragColor = color * vec4(1.0, 1.0, 1.0, uOpacity);
    }
  `;
  }

  get width() {
    return this.config.width;
  }

  get height() {
    return this.config.height;
  }

  get r() {
    return this.config.r || 0;
  }

  initElement() {
    this._element = new Graphics();

    if (this.config.gradient) {
      const shader = Shader.from(
        this.vertexShader,
        this.getFragmentShader(this.config.gradientDirection),
        {
          uStopCount: 0,
          uColors: new Float32Array(MAX_STOPS * 4),
          uStops: new Float32Array(MAX_STOPS),
          uOpacity: 1,
        },
      );
      const geometry = new Geometry()
        .addAttribute(
          'aVertexPosition', // the attribute name
          [
            0,
            0, // top-left
            1,
            0, // top-right
            1,
            1, // bottom-right
            0,
            1,
          ], // bottom-left
          2,
        ) // the size of the attribute (2D coordinates)
        .addAttribute(
          'aTextureCoord', // the attribute name
          [
            0,
            0, // top-left
            1,
            0, // top-right
            1,
            1, // bottom-right
            0,
            1,
          ], // bottom-left
          2,
        ) // the size of the attribute (texture coordinates)
        .addIndex([0, 1, 2, 0, 2, 3]);

      this.quad = new Mesh(geometry, shader);
      this.quad.scale.set(this.width, this.height);
      // (this.parent as GeneralShape).container.addChild(this.quad);
      // instead of (this.parent as GeneralShape).container.addChild(this.quad);
      this.container.addChild(this.quad);

      this.quad.mask = this.element;

      // initialize uniforms once
      this.updateGradientUniforms();
    }
  }

  private updateGradientUniforms() {
    if (!this.config.gradient || !this.quad) return;

    const shader = this.quad.shader as Shader;

    const stopsIn = (
      this.config.gradientStops?.length
        ? this.config.gradientStops
        : [
            { color: this.fill || '#ff0000', stop: 0 },
            { color: this.config.gradientColor || '#ffffff', stop: 1 },
          ]
    ) as GradientStop[];

    // console.log('stopsIn', stopsIn);

    const stops = normalizeStops(stopsIn).slice(0, MAX_STOPS);

    // console.log('stops', stops);
    const colorsArr = shader.uniforms.uColors as Float32Array;
    const stopsArr = shader.uniforms.uStops as Float32Array;

    // zero out
    colorsArr.fill(0);
    stopsArr.fill(0);

    for (let i = 0; i < stops.length; i++) {
      const [r, g, b, a] = hexToRgba01(stops[i].color, 1);
      colorsArr[i * 4 + 0] = r;
      colorsArr[i * 4 + 1] = g;
      colorsArr[i * 4 + 2] = b;
      colorsArr[i * 4 + 3] = a;
      stopsArr[i] = stops[i].stop;
    }

    shader.uniforms.uStopCount = stops.length;
    shader.uniforms.uOpacity =
      this.opacity === undefined && this.fillOpacity === undefined
        ? 1
        : (this.opacity ?? this.fillOpacity ?? 1);
  }

  updateAttr() {
    super.updateAttr();
    if (!this.element) return;

    // ... your existing drawing code ...

    if (this.config.gradient) {
      // IMPORTANT: build the mask geometry every update
      this.element.clear();

      // Mask doesn't care about color, only alpha > 0
      this.element.beginFill(0xffffff, 1);

      this.element
        .drawRoundedRect(this.px, this.py, this.width, this.height, this.r)
        .endFill();

      this.updateGradientUniforms();

      // choose ONE opacity path:
      // Option A: control opacity via quad.alpha (recommended)
      const alpha =
        this.opacity === undefined && this.fillOpacity === undefined
          ? 1
          : (this.opacity ?? this.fillOpacity ?? 1);

      this.quad.alpha = alpha;

      this.quad.scale.set(this.width, this.height);
      return;
    } else {
      this.element
        .drawRoundedRect(this.px, this.py, this.width, this.height, this.r)
        .endFill();
    }
  }

  prev: number;
  patch(
    config: Partial<SVGConfig<{ width: number; height: number; r?: number }>>,
    show?: boolean,
  ): void {
    if (config.height < this.height) {
      // console.warn('decrease --------------');
    }
    this.prev = config.height;
    super.patch(config, show);
    // console.log('rectangle-element > patch', config);
    if (config.position) {
      this.element.moveTo(config.position.x, config.position.y);
    } else {
      this.element.moveTo(0, 0);
    }
    // console.log('rectangle-element > patch', config);
  }
}
